import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useT, useLanguage } from "@/i18n/LanguageContext";

/**
 * Terms & Conditions page.
 *
 * Renders the full single-file HTML provided by legal counsel inside an
 * iframe — this keeps the document's embedded fonts and styles isolated from
 * the rest of the app and makes future swaps trivial: just overwrite the
 * file at /public/legal/terms-{en,sr}.html.
 *
 * The iframe source flips with the current language toggle.
 */
const T = {
  back: { en: "Back to site", sr: "Nazad na sajt" },
  title: { en: "Terms & Conditions", sr: "Uslovi korišćenja" },
  loading: { en: "Loading the document…", sr: "Učitavanje dokumenta…" },
};

// Document version metadata — update these two values whenever the terms change.
const DOC_VERSION = "1.0";
const DOC_EFFECTIVE = { en: "25 June 2026", sr: "25. jun 2026." };
const VERSION_BANNER = {
  versionLabel: { en: "Version", sr: "Verzija" },
  effectiveLabel: { en: "Effective", sr: "Važi od" },
};

export default function Terms() {
  const t = useT();
  const { lang } = useLanguage();
  const [loaded, setLoaded] = useState(false);
  const src = lang === "sr" ? "/legal/terms-sr.html" : "/legal/terms-en.html";

  return (
    <main className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F] flex flex-col">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#62646B] hover:text-[#0A0A0F]"
          >
            <ArrowLeft size={14} /> {t(T.back)}
          </Link>
          <h1 className="font-heading font-semibold tracking-tight text-base sm:text-lg">
            {t(T.title)}
          </h1>
          <div className="flex items-center gap-3">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold tracking-tight hidden sm:inline">$WINN</span>
          </div>
        </div>
      </header>

      <div
        data-testid="terms-version-banner"
        className="bg-[#FBF8F1] border-b border-[#E8E7E2]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[#62646B]">
          <span className="inline-flex items-center gap-1.5 font-semibold text-[#0A0A0F]">
            {t(VERSION_BANNER.versionLabel)} {DOC_VERSION}
          </span>
          <span className="hidden sm:inline text-[#D7D5CE]">·</span>
          <span className="inline-flex items-center gap-1.5">
            {t(VERSION_BANNER.effectiveLabel)} {t(DOC_EFFECTIVE)}
          </span>
        </div>
      </div>

      <div className="flex-1 relative">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#62646B] z-10 bg-[#FAFAF7]">
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> {t(T.loading)}
            </span>
          </div>
        )}
        <iframe
          key={src}
          src={src}
          title={t(T.title)}
          data-testid="terms-iframe"
          onLoad={() => setLoaded(true)}
          className="w-full h-full min-h-[60vh] border-0 bg-white"
        />
      </div>
    </main>
  );
}
